import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

type AddressValue = {
  street?: string | null;
  zip_code?: string | null;
  city?: string | null;
  country?: string | null;
  email_counter_reminders?: string | null;
};

type GridAddressCellProps = {
  address?: AddressValue | null;
  addressText?: string | null;
  singleLine?: boolean;
  /** Shown in place of the address when there is none - equipment that is not
   *  installed at a client's site sits with the workspace's own company. */
  fallbackText?: string | null;
};

const normalizePart = (value?: string | null) => value?.trim() || "";

/** Italicised so it reads as "where the machine is" rather than as a street
 *  address someone entered. */
function FallbackLocation({ text, singleLine = false }: { text: string; singleLine?: boolean }) {
  return (
    <Typography
      variant={singleLine ? "caption" : "body2"}
      color="text.secondary"
      sx={{ display: "block", lineHeight: 1.1, fontStyle: "italic" }}
    >
      {text}
    </Typography>
  );
}

export default function GridAddressCell({
  address,
  addressText,
  singleLine = false,
  fallbackText,
}: GridAddressCellProps) {
  const { t } = useTranslation();
  const street = normalizePart(address?.street);
  const zipCode = normalizePart(address?.zip_code);
  const city = normalizePart(address?.city);
  const reminderEmail = normalizePart(address?.email_counter_reminders);
  const formattedAddressText = normalizePart(addressText);
  const fallback = normalizePart(fallbackText);

  if (singleLine) {
    if (!formattedAddressText) {
      return fallback ? <FallbackLocation text={fallback} singleLine /> : null;
    }

    return (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", lineHeight: 1.1 }}
      >
        {formattedAddressText}
      </Typography>
    );
  }

  const locationParts = [zipCode, city]
    .filter(Boolean)
    .join(" ");

  if (!street && !locationParts && !reminderEmail) {
    return fallback ? <FallbackLocation text={fallback} /> : null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
      {street ? (
        <Typography variant="body2" sx={{ lineHeight: 1.1 }}>
          {street}
        </Typography>
      ) : null}
      {locationParts ? (
        <Typography variant="caption" color="text.secondary">
          {locationParts}
        </Typography>
      ) : null}
      {reminderEmail ? (
        <Typography variant="caption" color="text.secondary">
          {`${t("Email for counter reminders")}: ${reminderEmail}`}
        </Typography>
      ) : null}
    </Box>
  );
}
